const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://checker-api.podus.org/api/v1'

const fallbackOrigin =
  typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'http://localhost'

const backendOrigin = new URL(API_BASE_URL, fallbackOrigin).origin

const isAbsoluteUrl = (value: string) =>
  value.startsWith('http://') ||
  value.startsWith('https://') ||
  value.startsWith('blob:') ||
  value.startsWith('data:') ||
  value.startsWith('//')

export const resolveBackendUrl = (value?: string | null) => {
  if (!value) return ''
  if (isAbsoluteUrl(value)) return value
  const path = value.startsWith('/') ? value : `/${value}`
  return `${backendOrigin}${path}`
}

/** Extract a YouTube video ID from various URL formats */
export const getYouTubeId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?.*v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
  ]
  for (const re of patterns) {
    const m = url.match(re)
    if (m?.[1]) return m[1]
  }
  return null
}

/** Extract a Vimeo video ID */
export const getVimeoId = (url: string): string | null => {
  const m = url.match(/vimeo\.com\/(\d+)/)
  return m?.[1] ?? null
}

/** Check if a URL is a YouTube or Vimeo link */
export const isExternalVideo = (url?: string | null): boolean => {
  if (!url) return false
  return !!getYouTubeId(url) || !!getVimeoId(url)
}

/** Get an embeddable URL for YouTube/Vimeo (with JS API enabled for progress tracking) */
export const getEmbedUrl = (url: string): string | null => {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const ytId = getYouTubeId(url)
  if (ytId) return `https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0&enablejsapi=1&origin=${encodeURIComponent(origin)}`
  const vimeoId = getVimeoId(url)
  if (vimeoId) return `https://player.vimeo.com/video/${vimeoId}?autoplay=1`
  return null
}

/** Check if URL is YouTube specifically */
export const isYouTubeVideo = (url?: string | null): boolean => {
  if (!url) return false
  return !!getYouTubeId(url)
}

/** Check if URL is Vimeo specifically */
export const isVimeoVideo = (url?: string | null): boolean => {
  if (!url) return false
  return !!getVimeoId(url)
}

/** Get a thumbnail URL for YouTube/Vimeo */
export const getExternalThumbnail = (url: string): string | null => {
  const ytId = getYouTubeId(url)
  if (ytId) return `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`
  // Vimeo requires an API call for thumbnails — skip
  return null
}
